// src/components/pages/ChallengeSubmissionPage.js

import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Grid,
  Snackbar,
  Typography,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { useTheme } from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import useChallengeData from "../../hooks/useChallengeData";
import useUserProfile from "../../hooks/useUserProfile";
import useDraftSubmission from "../../hooks/useDraftSubmission";
import usePublishSubmission from "../../hooks/usePublishSubmission";
import useFormFields from "../../hooks/useFormFields";
import { validateSubmissionFields } from "../../utils/validationUtils";
import { saveDraft } from "../../utils/draftUtils";
import { processSubmission } from "../../utils/submissionUtils";
import { updateArtworkMetadata } from "../../utils/api";
import {
  handleTextChange,
  handleNumberChange,
} from "../../utils/inputHandlers";
import { initiatePurchase } from "../../services/purchaseService";
import CustomCard from "../molecules/ChallengeCreator/CustomCard";
import TitleInput from "../molecules/FormFields/TitleInput";
import DescriptionInput from "../molecules/FormFields/DescriptionInput";
import FundingGoalInput from "../molecules/FormFields/FundingGoalInput";
import TagsInput from "../molecules/FormFields/TagsInput";
import VideoUpload from "../molecules/FormFields/VideoUpload";
import PayPalPayment from "../molecules/PayPalPayment";
import SubmissionActions from "../molecules/SubmissionActions";
import ChallengeDetailHeader from "../organisms/ChallengeDetailHeader";

const ChallengeSubmissionPage = () => {
  const { challengeId, submissionId } = useParams(); // Combined useParams
  const { currentUser } = useAuth();
  const theme = useTheme();
  const [formErrors, setFormErrors] = useState({});
  const [showPayPalButtons, setShowPayPalButtons] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const navigate = useNavigate();

  const {
    challenge: challengeData, // Aliased to challengeData
    loading: challengeLoading,
    error: challengeError,
    // Other returned properties if needed
  } = useChallengeData(challengeId, currentUser);

  const {
    profileData,
    loading: profileLoading,
    error: profileError,
  } = useUserProfile(currentUser?.uid);

  const {
    draftData,
    loading: draftLoading,
    error: draftError,
  } = useDraftSubmission(submissionId); // Real-time updates

  const hasEnded = challengeData
    ? new Date(challengeData.endDate) < new Date()
    : false;

  const {
    title,
    setTitle,
    description,
    setDescription,
    fundingGoal,
    setFundingGoal,
    digitalSupply,
    setDigitalSupply,
    tags,
    setTags,
  } = useFormFields(draftData);

  // Separate state for videoUploaded
  const [videoUploaded, setVideoUploaded] = useState(
    draftData?.videoUploaded || false
  );

  // New state for tracking video uploading
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);

  const saveDraftText = draftData ? "Save Changes" : "Save Draft";

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Handle tag addition
  const handleTagAddition = (tag) => {
    const updatedTags = [...tags, tag];
    setTags(updatedTags);
  };

  // Handle tag deletion
  const handleTagDelete = (i) => {
    const updatedTags = tags.filter((tag, index) => index !== i);
    setTags(updatedTags);
  };

  const handleFileSelected = (fileData) => {
    // Update the draft with the selected file information
    const { submissionSaleId, saleId, fileName, contentType } = fileData;

    // Add the selected file data to the draftPayload without altering videoUploaded
    handleSaveDraft(false, { submissionSaleId, saleId, fileName, contentType });
  };

  const handleVideoUploaded = (isUploaded) => {
    if (isUploaded) {
      handleSaveDraft(true, { videoUploaded: true });
      setVideoUploaded(true);
    } else {
      setSnackbarMessage("Failed to upload the video. Please try again.");
      setSnackbarOpen(true);
    }
  };

  // Handler to update isUploadingVideo state
  const handleUploadingChange = (uploading) => {
    setIsUploadingVideo(uploading);
  };

  // Synchronize videoUploaded state with draftData.videoUploaded
  useEffect(() => {
    const newVideoUploaded = draftData?.videoUploaded || false;
    setVideoUploaded(newVideoUploaded);
  }, [draftData]);

  // Handle the save draft action
  const handleSaveDraft = async (uploaded = false, additionalData = {}) => {
    if (!challengeData) {
      setSnackbarMessage("Challenge data is missing. Please try again.");
      setSnackbarOpen(true);
      return;
    }

    // Determine the current value of videoUploaded based on the 'uploaded' parameter or component state
    const currentVideoUploaded = uploaded ? true : videoUploaded;

    // Prepare the draft payload
    const draftPayload = {
      ...draftData, // Preserve existing data
      title,
      description,
      fundingGoal,
      tags,
      userId: currentUser.uid,
      status: "draft",
      updatedAt: new Date(),
      challengeData: { ...challengeData },
      profileData: { ...profileData },
      ...additionalData, // Merge additional data like submissionSaleId, saleId, fileName, contentType
      videoUploaded: currentVideoUploaded, // Explicitly set videoUploaded
      ...(challengeData &&
        !challengeData.allowCrowdFunding && { digitalSupply }),
    };

    try {
      if (submissionId) {
        // Update existing draft
        await saveDraft({
          submissionId,
          currentUser,
          draftPayload,
          setSnackbarMessage,
        });
      } else {
        // Create new draft
        const draftIdStorageKey = `draft_${challengeId}`;
        await saveDraft({
          submissionId, // Undefined, indicating a new draft
          currentUser,
          draftPayload,
          draftIdStorageKey,
          setSnackbarMessage,
        });
      }

      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error saving draft:", error);
      setSnackbarMessage("Failed to save draft. Please try again.");
      setSnackbarOpen(true);
    }
  };

  const handleProcessSubmission = async (paymentData) => {
    await processSubmission({
      submissionId,
      paymentData,
      challengeId,
      currentUser,
      title,
      description,
      fundingGoal,
      tags,
      challengeData,
      profileData,
      setSnackbarMessage,
      setSnackbarOpen,
      saveDraft,
      initiatePurchase,
      draftData, // Ensure draftData is passed
    });
  };

  const {
    publishSubmission,
    loading: publishLoading,
    error: publishError,
  } = usePublishSubmission();

  // Handle submission fee requirements
  useEffect(() => {
    if (challengeData) {
      const { chargeSubmissionFee, submissionFee } = challengeData;

      // Show PayPal buttons if a submission fee is required and the fee is greater than 0
      if (chargeSubmissionFee && submissionFee > 0) {
        setShowPayPalButtons(true);
      } else {
        setShowPayPalButtons(false);
      }
    }
  }, [challengeData]);

  // Handle payment completion
  useEffect(() => {
    if (draftData) {
      if (draftData.status === "published") {
        setIsPublished(true);
      }

      if (draftData.paymentData?.status === "COMPLETED") {
        setShowPayPalButtons(false);
      }
    }
  }, [draftData]);

  const handleTitleChange = handleTextChange(
    setTitle,
    "title",
    formErrors,
    setFormErrors
  );

  const handleDescriptionChange = handleTextChange(
    setDescription,
    "description",
    formErrors,
    setFormErrors
  );

  const handleFundingGoalChange = handleNumberChange(
    setFundingGoal,
    "fundingGoal",
    formErrors,
    setFormErrors
  );

  const handleDigitalSupplyChange = handleNumberChange(
    setDigitalSupply,
    "digitalSupply",
    formErrors,
    setFormErrors,
    2000, // set an appropriate max for digitalSupply
    1 // minimum value for digitalSupply
  );

  const handleSubmit = async () => {
    const fields = {
      title,
      description,
      fundingGoal,
      tags,
      ...(challengeData &&
        !challengeData.allowCrowdFunding && { digitalSupply }),
    };

    const errors = validateSubmissionFields(fields);

    if (Object.keys(errors).length > 0) {
      // If there are validation errors, display them
      setSnackbarMessage("Please correct the highlighted errors.");
      setSnackbarOpen(true);
      setFormErrors(errors); // Update the state with validation errors
      return;
    }

    try {
      // No validation errors, proceed with updating the submission status
      if (!submissionId) {
        setSnackbarMessage(
          "Draft ID not found. Please try saving the draft first."
        );
        setSnackbarOpen(true);
        return;
      }

      // Ensure draftData has the latest values
      if (!draftData) {
        setSnackbarMessage("Draft data is not available. Please try again.");
        setSnackbarOpen(true);
        return;
      }

      // Call the updateArtworkMetadata API function
      const updatedMetadata = await updateArtworkMetadata(fields, draftData);

      console.log("updatedMetadata: ", updatedMetadata);

      // Proceed to publish the submission
      const success = await publishSubmission(submissionId, fields);

      if (success) {
        setIsPublished(true);
        setSnackbarMessage("Submission successfully published.");
        navigate(`/challenge/submission/${submissionId}`);
      } else {
        setSnackbarMessage("Failed to publish submission. Please try again.");
      }
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error publishing submission:", error);
      setSnackbarMessage("Failed to publish submission. Please try again.");
      setSnackbarOpen(true);
    }
  };

  // Loading state: show a loading indicator if any of the data is loading
  if (challengeLoading || profileLoading || draftLoading) {
    return (
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1, // Ensures it's above other elements
          backgroundColor: "rgba(0, 0, 0, 0.5)", // Slightly opaque black
        }}
        open={true} // Always open when in this loading state
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  // Error state: show an error message if any of the errors occur
  const combinedError =
    challengeError || profileError || draftError || publishError;
  if (combinedError) {
    return (
      <Snackbar
        open
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={
          combinedError.message || "An error occurred. Please try again."
        }
      />
    );
  }

  return (
    <Container maxWidth="md">
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1, // Ensure it's above other elements
          backgroundColor: "rgba(0, 0, 0, 0.5)", // Slightly opaque black
        }}
        open={publishLoading} // Control visibility based on publishLoading
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <ChallengeDetailHeader
        challenge={challengeData}
        challengeId={challengeId}
        user={profileData}
        navigate={navigate}
        theme={theme}
        hasEnded={hasEnded}
      />
      {!hasEnded && (
        <Box
          sx={{
            backgroundColor: theme.colors.mediumPurple,
            borderRadius: "10px",
          }}
        >
          <CustomCard title="Submission Details">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TitleInput
                  value={title}
                  onChange={handleTitleChange}
                  disabled={isPublished}
                  error={formErrors.title}
                />
              </Grid>
              <Grid item xs={12}>
                <DescriptionInput
                  value={description}
                  onChange={handleDescriptionChange}
                  disabled={isPublished}
                  error={formErrors.description}
                />
              </Grid>
              <Grid item xs={12}>
                <FundingGoalInput
                  challenge={challengeData}
                  value={{ fundingGoal, digitalSupply }}
                  onChange={(e) => {
                    const { name } = e.target;
                    if (name === "fundingGoal") {
                      handleFundingGoalChange(e);
                    } else if (name === "digitalSupply") {
                      handleDigitalSupplyChange(e);
                    }
                  }}
                  disabled={isPublished}
                  error={formErrors.fundingGoal}
                />
              </Grid>
              <Grid item xs={12}>
                <TagsInput
                  tags={tags}
                  handleTagDelete={handleTagDelete}
                  handleTagAddition={handleTagAddition}
                  disabled={isPublished}
                />
              </Grid>
            </Grid>
          </CustomCard>
        </Box>
      )}

      {!isPublished && !hasEnded && (
        <Box
          sx={{
            backgroundColor: theme.colors.mediumPurple,
            borderRadius: "10px",
          }}
        >
          <CustomCard title="Submission Media">
            {videoUploaded ? (
              <Typography
                variant="body1"
                align="center"
                sx={{ fontFamily: "basic-sans", color: "white" }}
              >
                {draftData?.fileName} successfully uploaded
              </Typography>
            ) : (
              <VideoUpload
                onUpload={handleVideoUploaded} // Pass the correct handler
                onFileSelected={handleFileSelected}
                onUploadingChange={handleUploadingChange} // Pass the new handler
              />
            )}
          </CustomCard>
        </Box>
      )}

      {showPayPalButtons && !hasEnded && (
        <PayPalPayment
          submissionFee={challengeData.submissionFee}
          onPaymentSuccess={handleProcessSubmission}
          onPaymentError={(err) => console.error("Payment error:", err)}
          setSnackbarMessage={setSnackbarMessage}
          setSnackbarOpen={setSnackbarOpen}
          setShowPayPalButtons={setShowPayPalButtons}
          theme={theme}
        />
      )}

      {draftData?.paymentData?.status === "COMPLETED" && (
        <CustomCard title="Submission Fee">
          <Typography
            variant="body1"
            align="center"
            sx={{ fontFamily: "basic-sans", color: "white" }}
          >
            ${draftData?.challengeData.submissionFee} payment successful
          </Typography>
        </CustomCard>
      )}

      {!hasEnded && (
        <SubmissionActions
          onSaveDraft={() => handleSaveDraft()} // Ensure no parameters are passed
          onSubmit={handleSubmit}
          saveDraftText={saveDraftText}
          videoUploaded={videoUploaded}
          paySubmissionFee={showPayPalButtons}
          isPublishing={publishLoading} // Pass the publishLoading state
          disabled={isPublished || isUploadingVideo} // Include isUploadingVideo
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default ChallengeSubmissionPage;
